// Prompt for "Edit image" on an AI Studio grid tile (POR-14659). IMAGE 1 is the
// version on screen, sent as the base to edit; any product / reference images the
// user added follow it in that order. Editable overlay text is NOT drawn — it
// becomes a text layer at Create Layout, like everywhere else in AI Studio.
//
// Pure (no imports) so it can be verified with `node --test`.

function imageRange(start, count) {
  return count === 1 ? `IMAGE ${start}` : `IMAGES ${start}-${start + count - 1}`;
}

export function buildPdpEditPrompt({
  userInstructions = '',
  overlayText = '',
  overlayTextMode = 'editable',
  productImageCount = 0,
  referenceImageCount = 0,
  hasBrandLogo = false,
  language = null,
} = {}) {
  const lines = [
    'Edit IMAGE 1. It is the current version of a product detail page (PDP) image.',
    'Keep everything that is not mentioned below exactly as it is: composition, framing, the product and its label, colors, lighting and any existing text.',
  ];

  const direction = String(userInstructions || '').trim();
  const bakedText = overlayTextMode === 'part_of_image' ? String(overlayText || '').trim() : '';

  if (direction) {
    lines.push(`Apply this change: ${direction}`);
  }

  if (bakedText) {
    lines.push(`Add this text to the image, spelled exactly and clearly legible: "${bakedText}". Place it where it does not cover the product.`);
  }

  if (!direction && !bakedText) {
    lines.push('Make only subtle quality refinements; the result must stay recognisably the same image.');
  }

  let next = 2;

  if (productImageCount > 0) {
    lines.push(`${imageRange(next, productImageCount)} show the product: wherever the product appears, keep it faithful to ${productImageCount > 1 ? 'them' : 'it'}.`);
    next += productImageCount;
  }

  if (referenceImageCount > 0) {
    lines.push(`${imageRange(next, referenceImageCount)} ${referenceImageCount > 1 ? 'are style references' : 'is a style reference'}: make the requested change match ${referenceImageCount > 1 ? 'their' : 'its'} look.`);
  }

  if (hasBrandLogo) {
    lines.push('A brand logo is provided: place it once, small and unobtrusive, without altering it.');
  }

  if (language && String(language).trim()) {
    lines.push(`Any new text must be written in ${String(language).trim()}.`);
  }

  lines.push('Output a single image.');

  return lines.join('\n');
}

// "Edit image" with the base removed is a fresh configured generation. Empty popup
// fields mean "keep what this tile already has", not "clear it" — the popup starts
// empty, so treating blanks as clears wiped the tile's direction, overlay text and
// references on every such edit. Product images / logo are omitted when empty so the
// configured path falls back to the tile's own. (POR-14659)
export function buildNoBaseEditConfig(tile = {}, edit = {}) {
  const direction = String(edit.userInstructions || '').trim();
  const text = String(edit.overlayText || '').trim();
  const references = (edit.referenceImageUrls || []).filter(Boolean);
  const products = (edit.productImageUrls || []).filter(Boolean);

  const config = {
    userInstructions: direction || tile.userInstructions || null,
    overlayText: text || tile.overlayText || null,
    overlayTextMode: text
      ? (edit.overlayTextMode === 'part_of_image' ? 'part_of_image' : 'editable')
      : (tile.overlayTextMode || 'editable'),
    referenceImageUrls: references.length ? references : (tile.referenceImageUrls || []).filter(Boolean),
  };

  if (products.length) config.productImageUrls = products;
  if (edit.brandLogoUrl) config.brandLogoUrl = edit.brandLogoUrl;

  return config;
}
